import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { X, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

export type FieldValidator = (
  value: string | boolean | string[],
  operator: string,
) => { valid: boolean; error?: string }

export type FieldFormatter = {
  format: (value: string) => string
  parse: (value: string) => string
}

export type FieldNormalizer = (value: string) => string

export interface FieldConfig {
  value: string
  label: string
  type: 'string' | 'boolean' | 'enum'
  enumValues?: Array<{ value: string; label: string }>
  validator?: FieldValidator
  formatter?: FieldFormatter
  normalizer?: FieldNormalizer
}

export interface OperatorConfig {
  value: string
  label: string
  allowedForTypes?: Array<'string' | 'boolean' | 'enum'>
}

export interface Condition {
  id?: string
  field: string
  operator: string
  value: string | boolean | string[]
}

interface ConditionBuilderProps {
  fields: FieldConfig[]
  operators: OperatorConfig[]
  conditions: Condition[]
  onChange: (conditions: Condition[]) => void
  addButtonLabel?: string
  emptyStateText?: string
  className?: string
  getOperatorsForField?: (field: string) => OperatorConfig[]
  onValidationChange?: (hasErrors: boolean) => void
}

export function ConditionBuilder({
  addButtonLabel = 'Add Rule',
  className,
  conditions,
  fields,
  operators,
  onChange,
  onValidationChange,
  getOperatorsForField,
}: ConditionBuilderProps) {
  // Track validation errors by condition ID instead of index
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({})

  const updateValidationError = (conditionId: string, error: string | null) => {
    const newErrors = { ...validationErrors }
    if (error) {
      newErrors[conditionId] = error
    } else {
      delete newErrors[conditionId]
    }
    setValidationErrors(newErrors)
    onValidationChange?.(Object.keys(newErrors).length > 0)
  }

  const handleAddCondition = () => {
    const newCondition: Condition = {
      id: crypto.randomUUID(),
      field: '',
      operator: '',
      value: '',
    }
    onChange([...conditions, newCondition])
  }

  const handleRemoveCondition = (index: number) => {
    const removedCondition = conditions[index]

    // Remove the condition from the list
    onChange(conditions.filter((_, i) => i !== index))

    // Clear any validation error for the removed condition
    if (removedCondition.id) {
      const newErrors = { ...validationErrors }
      delete newErrors[removedCondition.id]
      setValidationErrors(newErrors)
      onValidationChange?.(Object.keys(newErrors).length > 0)
    }
  }

  const handleUpdateCondition = (
    index: number,
    updates: Partial<Condition>,
  ) => {
    onChange(
      conditions.map((condition, i) =>
        i === index ? { ...condition, ...updates } : condition,
      ),
    )
  }

  const getFieldConfig = (fieldValue: string): FieldConfig | undefined => {
    return fields.find(f => f.value === fieldValue)
  }

  const getAvailableOperators = (fieldValue: string): OperatorConfig[] => {
    // If custom getOperatorsForField is provided, use it (takes precedence)
    if (getOperatorsForField) {
      return getOperatorsForField(fieldValue)
    }

    // Otherwise, fall back to type-based filtering
    const fieldConfig = getFieldConfig(fieldValue)
    if (!fieldConfig) return operators

    return operators.filter(op => {
      if (!op.allowedForTypes) return true
      return op.allowedForTypes.includes(fieldConfig.type)
    })
  }

  if (conditions.length === 0) {
    return (
      <div className={cn('space-y-4', className)}>
        <Button onClick={handleAddCondition} variant="outline" size="sm">
          <Plus className="h-4 w-4 mr-2" />
          {addButtonLabel}
        </Button>
      </div>
    )
  }

  return (
    <div className={cn('space-y-3', className)}>
      {conditions.map((condition, index) => (
        <ConditionRow
          key={condition.id || index}
          condition={condition}
          fields={fields}
          operators={operators}
          onUpdate={updates => handleUpdateCondition(index, updates)}
          onRemove={() => handleRemoveCondition(index)}
          getFieldConfig={getFieldConfig}
          getAvailableOperators={getAvailableOperators}
          validationError={
            condition.id ? validationErrors[condition.id] : undefined
          }
          onValidationError={error =>
            condition.id
              ? updateValidationError(condition.id, error)
              : undefined
          }
        />
      ))}

      <Button onClick={handleAddCondition} variant="outline" size="sm">
        <Plus className="h-4 w-4 mr-2" />
        {addButtonLabel}
      </Button>
    </div>
  )
}

interface ConditionRowProps {
  condition: Condition
  fields: FieldConfig[]
  operators: OperatorConfig[]
  onUpdate: (updates: Partial<Condition>) => void
  onRemove: () => void
  getFieldConfig: (fieldValue: string) => FieldConfig | undefined
  getAvailableOperators: (fieldValue: string) => OperatorConfig[]
  validationError?: string
  onValidationError: (error: string | null) => void
}

function ConditionRow({
  condition,
  fields,
  operators,
  onUpdate,
  onRemove,
  getFieldConfig,
  getAvailableOperators,
  validationError,
  onValidationError,
}: ConditionRowProps) {
  const [fieldOpen, setFieldOpen] = useState(!condition.field)
  const [operatorOpen, setOperatorOpen] = useState(false)
  const [valueOpen, setValueOpen] = useState(false)
  const [showValidationError, setShowValidationError] = useState(false)

  const fieldConfig = getFieldConfig(condition.field)
  const availableOperators = getAvailableOperators(condition.field)
  const selectedField = fields.find(f => f.value === condition.field)
  const selectedOperator = operators.find(op => op.value === condition.operator)

  // Validate value - always run validation but only show error if field has been touched
  const validateValue = (
    value: string | boolean | string[],
    shouldShow = false,
  ) => {
    if (!fieldConfig?.validator || !condition.operator) {
      onValidationError(null)
      return
    }

    const result = fieldConfig.validator(value, condition.operator)
    onValidationError(result.valid ? null : result.error || 'Invalid value')

    if (shouldShow) {
      setShowValidationError(true)
    }
  }

  // Auto-select if only one field option on mount
  useEffect(() => {
    if (!condition.field && fields.length === 1) {
      const fieldValue = fields[0].value
      const newFieldConfig = getFieldConfig(fieldValue)
      onUpdate({
        field: fieldValue,
        operator: '',
        value: newFieldConfig?.type === 'boolean' ? false : '',
      })
    }
  }, [])

  // Auto-select if only one operator option when field changes
  useEffect(() => {
    if (
      condition.field &&
      !condition.operator &&
      availableOperators.length === 1
    ) {
      onUpdate({ operator: availableOperators[0].value })
      // Open value dropdown after state updates
      setValueOpen(true)
    }
  }, [condition.field])

  const handleFieldSelect = (fieldValue: string) => {
    const newFieldConfig = getFieldConfig(fieldValue)
    const newValue = newFieldConfig?.type === 'boolean' ? false : ''

    onUpdate({
      field: fieldValue,
      operator: '',
      value: newValue,
    })
    setFieldOpen(false)

    // Reset validation state when field changes
    onValidationError(null)
    setShowValidationError(false)

    // Check if only one operator available, auto-select it
    const newAvailableOperators = getAvailableOperators(fieldValue)
    if (newAvailableOperators.length === 1) {
      // Will be handled by useEffect
    } else {
      // Auto-open operator dropdown after field is selected
      setOperatorOpen(true)
    }
  }

  const handleOperatorSelect = (operatorValue: string) => {
    onUpdate({ operator: operatorValue })
    setOperatorOpen(false)

    // Re-validate with new operator if we already have a value
    if (condition.value && fieldConfig?.validator) {
      validateValue(condition.value, showValidationError)
    }

    // Auto-open value dropdown after operator is selected
    setValueOpen(true)
  }

  const renderValuePill = () => {
    if (!fieldConfig) return null

    // Check if value is set (for styling active state)
    const hasValue =
      condition.value !== '' &&
      condition.value !== null &&
      condition.value !== undefined &&
      (!Array.isArray(condition.value) || condition.value.length > 0)

    const hasError = showValidationError && validationError

    const baseClasses = cn(
      'relative h-9 px-4 text-sm transition-colors border rounded flex items-center focus:outline-none focus:ring-0 -ml-3',
      hasError
        ? 'bg-error-subtle text-error-emphasis hover:bg-error-muted border-error'
        : hasValue
          ? 'bg-muted text-foreground hover:bg-muted/80 border-border'
          : 'bg-muted/50 text-muted-foreground hover:bg-muted border-border',
    )

    // Boolean type
    if (fieldConfig.type === 'boolean') {
      return (
        <Popover open={valueOpen} onOpenChange={setValueOpen}>
          <PopoverTrigger asChild>
            <button className={baseClasses}>
              <div className="pl-3">
                {condition.value === true ? 'True' : 'False'}
              </div>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2" align="start">
            <div className="flex flex-col gap-1">
              <button
                onClick={() => {
                  onUpdate({ value: true })
                  setValueOpen(false)
                }}
                className={cn(
                  'px-3 py-2 text-sm rounded text-left hover:bg-muted transition-colors',
                  condition.value === true && 'bg-muted font-medium',
                )}
              >
                True
              </button>
              <button
                onClick={() => {
                  onUpdate({ value: false })
                  setValueOpen(false)
                }}
                className={cn(
                  'px-3 py-2 text-sm rounded text-left hover:bg-muted transition-colors',
                  condition.value === false && 'bg-muted font-medium',
                )}
              >
                False
              </button>
            </div>
          </PopoverContent>
        </Popover>
      )
    }

    // Enum type with multi-select for 'in' operator
    if (fieldConfig.type === 'enum' && fieldConfig.enumValues) {
      if (condition.operator === 'in') {
        const selectedValues = Array.isArray(condition.value)
          ? condition.value
          : []

        const selectedLabels = selectedValues
          .map(val => fieldConfig.enumValues?.find(e => e.value === val)?.label)
          .filter(Boolean)

        const displayText =
          selectedLabels.length > 0
            ? selectedLabels.join(' or ')
            : 'Select values'

        return (
          <Popover open={valueOpen} onOpenChange={setValueOpen}>
            <PopoverTrigger asChild>
              <button className={baseClasses}>
                <div className="pl-3">{displayText}</div>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-3" align="start">
              <div className="space-y-2">
                {fieldConfig.enumValues.map(enumOption => {
                  const isSelected = selectedValues.includes(enumOption.value)
                  return (
                    <label
                      key={enumOption.value}
                      className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 p-2 rounded"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={e => {
                          const newValues = e.target.checked
                            ? [...selectedValues, enumOption.value]
                            : selectedValues.filter(v => v !== enumOption.value)
                          onUpdate({ value: newValues })
                        }}
                        className="rounded border-border"
                      />
                      <span className="text-sm">{enumOption.label}</span>
                    </label>
                  )
                })}
              </div>
            </PopoverContent>
          </Popover>
        )
      }

      // Single select for regular enum operators
      const selectedEnum = fieldConfig.enumValues.find(
        e => e.value === condition.value,
      )
      return (
        <Popover open={valueOpen} onOpenChange={setValueOpen}>
          <PopoverTrigger asChild>
            <button className={baseClasses}>
              {selectedEnum?.label || 'Select value'}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2" align="start">
            <div className="flex flex-col gap-1">
              {fieldConfig.enumValues.map(enumOption => (
                <button
                  key={enumOption.value}
                  onClick={() => {
                    onUpdate({ value: enumOption.value })
                    setValueOpen(false)
                  }}
                  className={cn(
                    'px-3 py-2 text-sm rounded text-left hover:bg-muted transition-colors whitespace-nowrap',
                    condition.value === enumOption.value &&
                      'bg-muted font-medium',
                  )}
                >
                  {enumOption.label}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )
    }

    // String type - inline input with popover
    // Button displays formatted value, input always shows raw value
    const buttonDisplayValue = fieldConfig?.formatter
      ? fieldConfig.formatter.format((condition.value as string) || '')
      : (condition.value as string) || ''

    return (
      <Popover open={valueOpen} onOpenChange={setValueOpen}>
        <PopoverTrigger asChild>
          <button className={cn(baseClasses, 'max-w-[200px] truncate -ml-3')}>
            <div className="pl-3">{buttonDisplayValue || 'Enter value'}</div>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-3" align="start">
          <div className="space-y-2">
            <Input
              type="text"
              value={(condition.value as string) || ''}
              onChange={e => {
                onUpdate({ value: e.target.value })
                validateValue(e.target.value, false)
              }}
              onBlur={() => {
                // Normalize value if normalizer is provided
                const normalizedValue = fieldConfig?.normalizer
                  ? fieldConfig.normalizer(condition.value as string)
                  : condition.value

                if (normalizedValue !== condition.value) {
                  onUpdate({ value: normalizedValue })
                }

                validateValue(normalizedValue, true)
              }}
              placeholder="Enter value"
              autoFocus
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  // Normalize value if normalizer is provided
                  const normalizedValue = fieldConfig?.normalizer
                    ? fieldConfig.normalizer(condition.value as string)
                    : condition.value

                  if (normalizedValue !== condition.value) {
                    onUpdate({ value: normalizedValue })
                  }

                  validateValue(normalizedValue, true)
                  setValueOpen(false)
                }
              }}
              className={cn(
                showValidationError && validationError && 'border-error',
              )}
            />
            {showValidationError && validationError && (
              <p className="text-sm text-error">{validationError}</p>
            )}
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {/* Field Selector */}
      <Popover open={fieldOpen} onOpenChange={setFieldOpen}>
        <PopoverTrigger asChild>
          <button
            className={cn(
              'group relative h-9 px-4 text-sm transition-colors border rounded z-20',
              condition.field
                ? 'bg-muted text-foreground hover:bg-muted/80 border-border pr-6'
                : 'bg-muted/50 text-muted-foreground hover:bg-muted border-border',
            )}
          >
            {selectedField?.label || 'Select field'}
            {/* Arrow pointing right - only when field is set */}
            {condition.field && (
              <div className="absolute -right-[9px] top-0 bottom-0 w-0 h-0 border-t-[17px] border-t-transparent border-b-[17px] border-b-transparent border-l-[10px] border-l-border group-hover:border-l-muted-foreground/40 transition-colors z-10" />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2" align="start">
          <div className="flex flex-col gap-1">
            {fields.map(field => (
              <button
                key={field.value}
                onClick={() => handleFieldSelect(field.value)}
                className={cn(
                  'px-3 py-2 text-sm rounded text-left hover:bg-muted transition-colors whitespace-nowrap',
                  condition.field === field.value && 'bg-muted font-medium',
                )}
              >
                {field.label}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      {/* Operator Selector - only show if field is selected */}
      {condition.field && (
        <Popover open={operatorOpen} onOpenChange={setOperatorOpen}>
          <PopoverTrigger asChild>
            <button
              className={cn(
                'group relative h-9 px-4 text-sm transition-colors border rounded z-10 -ml-3',
                condition.operator
                  ? 'bg-gray-200 text-gray-800 hover:bg-gray-300 border-gray-200 pr-6'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border-gray-300',
              )}
            >
              <div className="pl-3">
                {selectedOperator?.label || 'Select operator'}
                {/* Arrow pointing right - only when operator is set */}
                {condition.operator && (
                  <div className="absolute -right-[9px] top-0 bottom-0 w-0 h-0 border-t-[17px] border-t-transparent border-b-[17px] border-b-transparent border-l-[10px] border-l-border group-hover:border-l-muted-foreground/40 transition-colors z-10" />
                )}
              </div>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2" align="start">
            <div className="flex flex-col gap-1">
              {availableOperators.map(op => (
                <button
                  key={op.value}
                  onClick={() => handleOperatorSelect(op.value)}
                  className={cn(
                    'px-3 py-2 text-sm rounded text-left hover:bg-muted transition-colors whitespace-nowrap',
                    condition.operator === op.value && 'bg-muted font-medium',
                  )}
                >
                  {op.label}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}

      {/* Value Input - only show if operator is selected */}
      {condition.field && condition.operator && renderValuePill()}

      {/* Remove Button */}
      <button
        onClick={onRemove}
        className="ml-2 p-1 hover:bg-muted rounded transition-colors"
        aria-label="Remove condition"
      >
        <X className="h-4 w-4 text-muted-foreground" />
      </button>
    </div>
  )
}
